/**
 * Naming convention utilities for FP&A Platform
 * Format: BUCode_FiscalYear_Type
 * Example: ANL_2025-2026_Budget
 */

/**
 * Generate a standardized name based on naming convention
 * @param {string} buCode - Business Unit code (e.g., "ANL")
 * @param {string} fiscalYear - Fiscal year (e.g., "2025/2026")
 * @param {string} type - Document type (e.g., "Budget", "BvA", "Forecast")
 * @returns {string} Formatted name
 */
export const generateStandardName = (buCode, fiscalYear, type) => {
  // Clean up fiscal year format (2025/2026 -> 2025-2026)
  const cleanFiscalYear = fiscalYear?.replace('/', '-') || '';
  const cleanBuCode = buCode?.toUpperCase() || '';
  const cleanType = type || 'Budget';
  
  return `${cleanBuCode}_${cleanFiscalYear}_${cleanType}`;
};

/**
 * Generate export filename with naming convention
 * @param {string} buCode - Business Unit code
 * @param {string} fiscalYear - Fiscal year
 * @param {string} type - Document type
 * @param {string} format - File format (xlsx, csv, pdf)
 * @returns {string} Formatted filename
 */
export const generateExportFilename = (buCode, fiscalYear, type, format = 'xlsx') => {
  const baseName = generateStandardName(buCode, fiscalYear, type);
  const timestamp = new Date().toISOString().split('T')[0];
  return `${baseName}_${timestamp}.${format}`;
};

/**
 * Parse a standardized name back to its components
 * @param {string} name - Standardized name
 * @returns {object} { buCode, fiscalYear, type }
 */
export const parseStandardName = (name) => {
  const parts = name?.split('_') || [];
  return {
    buCode: parts[0] || '',
    fiscalYear: parts[1]?.replace('-', '/') || '',
    type: parts[2] || ''
  };
};

/**
 * Get BU code from business unit object
 * @param {object} bu - Business unit object with name or code
 * @returns {string} BU code (first 3-4 letters of name if no code)
 */
export const getBuCode = (bu) => {
  if (!bu) return 'ALL';
  if (bu.code) return bu.code.toUpperCase();
  // Generate code from name (first 3-4 chars)
  const name = bu.name || '';
  return name.substring(0, 4).toUpperCase().replace(/\s+/g, '');
};

/**
 * Sort items alphabetically by a key
 * @param {Array} items - Array of items to sort
 * @param {string} key - Key to sort by
 * @returns {Array} Sorted array
 */
export const sortAlphabetically = (items, key = 'name') => {
  return [...items].sort((a, b) => {
    const aVal = (a[key] || '').toString().toLowerCase();
    const bVal = (b[key] || '').toString().toLowerCase();
    return aVal.localeCompare(bVal);
  });
};

/**
 * Sort dates newest to oldest
 * @param {Array} dates - Array of date strings
 * @returns {Array} Sorted array
 */
export const sortDatesNewestFirst = (dates) => {
  return [...dates].sort((a, b) => {
    // Handle fiscal year format (2025/2026)
    const aYear = parseInt(a.split('/')[0] || a.split('-')[0] || a);
    const bYear = parseInt(b.split('/')[0] || b.split('-')[0] || b);
    return bYear - aYear;
  });
};

/**
 * Sort fiscal years newest to oldest
 * @param {Array} fiscalYears - Array of fiscal year strings
 * @returns {Array} Sorted array
 */
export const sortFiscalYearsNewestFirst = (fiscalYears) => {
  return [...fiscalYears].sort((a, b) => {
    const aYear = parseInt(a?.split('/')[0] || a?.split('-')[0] || '0');
    const bYear = parseInt(b?.split('/')[0] || b?.split('-')[0] || '0');
    return bYear - aYear;
  });
};

export default {
  generateStandardName,
  generateExportFilename,
  parseStandardName,
  getBuCode,
  sortAlphabetically,
  sortDatesNewestFirst,
  sortFiscalYearsNewestFirst
};
